import { getAccessToken } from "../auth/auth";
import {
  initPostRequest,
  initFetchRequest,
  initDeleteRequest,
} from "../utils/helperFunctions";
import { fetchData, putData } from "../utils/helperFunctions";

// This function is used to post a new study for a doctor
export const postNewStudy = async (
  studyID,
  studyName,
  studyDescription,
  patients
) => {
  var token = await getAccessToken();
  var body = {
    StudyID: studyID,
    Name: studyName,
    Abstract: studyDescription,
    Patients: patients,
  };
  var request = initPostRequest("/api/study", token, JSON.stringify(body));

  try {
    const response = await putData(request);
    return { success: true, data: response };
  } catch (err) {
    console.error("Error calling the Web api:\n" + err);
    return {
      success: false,
      error: err instanceof Error ? err.message : "Unknown error occurred",
    };
  }
};

export const postNewNotification = async (
  messageText,
  actionText,
  patiensIds,
  tags,
  silent = false
) => {
  var token = await getAccessToken();
  try {
    return await Promise.all(
      patiensIds.map(async (userId) => {
        const body = {
          text: messageText,
          action: actionText,
          userId,
          tags,
          silent,
        };
        const request = initPostRequest(
          "/api/Notifications/requests",
          token,
          JSON.stringify(body)
        );
        return await putData(request);
      })
    );
  } catch (err) {
    console.error("Error calling the Web api:\n" + err);
  }
};

export const getAllStudyIDs = async () => {
  var token = await getAccessToken();
  var request = initFetchRequest(
    "/api/Study/GetUsersStudiesInformation",
    token
  );
  try {
    var response = await fetchData(request).then((value) => {
      if (value.$values !== undefined) {
        return value.$values.map((v) => v.idStudies);
      }
    });
    return response;
  } catch (err) {
    console.error("Error calling the Web api:\n" + err);
  }
};

export const getAllUserStudyInformation = async () => {
  var token = await getAccessToken();
  var request = initFetchRequest(
    "/api/Study/GetUsersStudiesInformation",
    token
  );
  try {
    var response = await fetchData(request);
    return response.$values;
  } catch (err) {
    console.error("Error calling the Web api:\n" + err);
  }
};

export const getAllStudyInformation = async (ids) => {
  var body = {
    studyIds: ids,
  };
  var token = await getAccessToken();
  var request = initPostRequest(
    "/api/study/GetStudiesInformation",
    token,
    JSON.stringify(body)
  );
  try {
    var response = await fetchData(request);
    return response;
  } catch (err) {
    console.error("Error calling the Web api:\n" + err);
  }
};

export const getStudyDashboardData = async () => {
  const token = await getAccessToken();
  const request = initFetchRequest(
    "/api/Study/GetWebsiteStudyDashboardData",
    token
  );
  try {
    var response = await fetchData(request);
    return response;
  } catch (err) {
    console.error("Error calling the Web api:\n" + err);
  }
};

export const getSpecificStudy = async (id) => {
  var token = await getAccessToken();
  var request = initFetchRequest("/api/Study/GetSpecificStudy/" + id, token);
  try {
    var response = await fetchData(request).then((value) => value.$values);
    return response;
  } catch (err) {
    console.error("Error calling the Web api:\n" + err);
  }
};

// For DOCTORS: Delete an entire study and all its data
// Endpoint: DELETE /api/study/{id}
export const deleteStudy = async (id) => {
  var token = await getAccessToken();
  var request = initDeleteRequest("/api/study/" + id, token);
  try {
    var response = await fetch(request);

    if (response.status === 204) {
      return { success: true };
    } else if (response.status === 403) {
      return {
        success: false,
        error: "Unauthorized: You do not have permission to delete this study.",
      };
    } else if (response.status === 404) {
      const errorText = await response.text();
      return {
        success: false,
        error: errorText || `No study found with ID ${id}`,
      };
    } else if (response.status === 500) {
      const errorText = await response.text();
      return {
        success: false,
        error: `Server error: ${errorText}`,
      };
    } else {
      return {
        success: false,
        error: `Unexpected response status: ${response.status}`,
      };
    }
  } catch (err) {
    console.error("Error calling the Web api:\n" + err);
    return {
      success: false,
      error: err instanceof Error ? err.message : "Unknown error occurred",
    };
  }
};

export const sendDoctorToPatientEmail = async (to, subject, emailBody) => {
  var token = await getAccessToken();
  var body = {
    emails: [to],
    subject: subject,
    body: emailBody,
  };
  var request = initPostRequest(
    "/api/DoctorsToPatients/SendEmailInvites",
    token,
    JSON.stringify(body)
  );
  try {
    await putData(request);
  } catch (err) {
    console.error("Error calling the Web api:\n" + err);
  }
};

export const sendPatientPayRequestEmail = async (subject, emailBody) => {
  var token = await getAccessToken();
  var body = {
    emails: ["contact-us@computingreapplied.com"],
    subject: subject,
    body: emailBody,
  };
  var request = initPostRequest(
    "/api/DoctorsToPatients/SendEmailInvites",
    token,
    JSON.stringify(body)
  );
  try {
    await putData(request);
  } catch (err) {
    console.error("Error calling the Web api:\n" + err);
  }
};

// Join a study by code
export const joinStudyByCode = async (code) => {
  var token = await getAccessToken();
  var body = code;
  var request = initPostRequest(
    "/api/Study/JoinByCode",
    token,
    JSON.stringify(body)
  );
  try {
    await fetchData(request);
  } catch (err) {
    console.error("Error calling the Web api:\n" + err);
  }
};

// For PATIENTS: Leave a study (remove enrollment)
// Endpoint: DELETE /api/Study/leave/{studyId}
export const leaveStudy = async (studyId) => {
  var token = await getAccessToken();
  var request = initDeleteRequest("/api/Study/leave/" + studyId, token);
  try {
    var response = await fetch(request);

    if (response.status === 204) {
      return { success: true };
    } else if (response.status === 403) {
      return {
        success: false,
        error: "You do not have permission to leave this study.",
      };
    } else if (response.status === 404) {
      const errorText = await response.text();
      return {
        success: false,
        error: errorText || `Study not found with ID ${studyId}`,
      };
    } else if (response.status === 500) {
      const errorText = await response.text();
      return {
        success: false,
        error: `Server error: ${errorText}`,
      };
    } else {
      return {
        success: false,
        error: `Unexpected response status: ${response.status}`,
      };
    }
  } catch (err) {
    console.error("Error leaving study:\n" + err);
    return {
      success: false,
      error: err instanceof Error ? err.message : "Unknown error occurred",
    };
  }
};
